import { type LOCALES, OPPOSITE_LOCALE } from '@/plugins/i18n';
import type { RouteLocationNormalizedLoadedGeneric, RouteRecordMultipleViews, RouteRecordMultipleViewsWithChildren, RouteRecordRaw, RouteRecordRedirect, RouteRecordSingleView, RouteRecordSingleViewWithChildren } from 'vue-router';
import { determineLocale, getAlternateBaseUrl, getLocation } from '@/utilities';
import { getAlternateCategorySlug, getAlternateChildLocationSlug, getAlternateLocationSlug, getArticle, getArticleCategory } from '@/api';

type LocalizedPaths = Record<LOCALES, string>;

// Racine des pages de constructeurs. Partagee avec les blueprints de route pour que le lien
// alterne pointe toujours vers la racine de la locale opposee.
export const BUILDERS_ROOT: LocalizedPaths = { 'en-CA': '/builders', 'fr-CA': '/constructeurs' };

export const GUIDES_ROOT: LocalizedPaths = { 'en-CA': '/guide', 'fr-CA': '/guide' };
export const GUIDES_AUTHORS_ROOT: LocalizedPaths = { 'en-CA': `${GUIDES_ROOT['en-CA']}/authors`, 'fr-CA': `${GUIDES_ROOT['fr-CA']}/auteurs` };

type RouteBlueprintSingleView = Omit<RouteRecordSingleView, 'path'> & { path: LocalizedPaths };
type RouteBlueprintSingleViewWithChildren = Omit<RouteRecordSingleViewWithChildren, 'path' | 'children'> & { path: LocalizedPaths; children: RouteBlueprint[] };
type RouteBlueprintMultipleViews = Omit<RouteRecordMultipleViews, 'path'> & { path: LocalizedPaths };
type RouteBlueprintMultipleViewsWithChildren = Omit<RouteRecordMultipleViewsWithChildren, 'path' | 'children'> & { path: LocalizedPaths; children: RouteBlueprint[] };
type RouteBlueprintRedirect = Omit<RouteRecordRedirect, 'path'> & { path: LocalizedPaths };
export type RouteBlueprint = RouteBlueprintSingleView | RouteBlueprintSingleViewWithChildren | RouteBlueprintMultipleViews | RouteBlueprintMultipleViewsWithChildren | RouteBlueprintRedirect;

interface AlternateMeta {
  hreflang: string;
  href: string;
}

function isRedirect(route: RouteBlueprint): route is RouteBlueprintRedirect {
  return 'redirect' in route;
}

function isSingleViewWithChildren(route: RouteBlueprint): route is RouteBlueprintSingleViewWithChildren {
  return 'children' in route && 'component' in route;
}

function isMultipleViewsWithChildren(route: RouteBlueprint): route is RouteBlueprintMultipleViewsWithChildren {
  return 'children' in route && 'components' in route;
}

export function generateRoutes(blueprints: RouteBlueprint[], locale: LOCALES): RouteRecordRaw[] {
  const oppositeLocale = OPPOSITE_LOCALE[locale];
  const alternateBaseUrl = getAlternateBaseUrl();
  return blueprints.map((blueprint): RouteRecordRaw => {
    // REDIRECTS
    if (isRedirect(blueprint)) {
      const { path, ...rest } = blueprint;
      return {
        ...rest,
        path: path[locale],
        meta: {
          ...(rest.meta ?? {}),
          alternate: {
            hreflang: oppositeLocale,
            href: `${alternateBaseUrl}${path[oppositeLocale]}`,
          } as AlternateMeta,
        },
      };
    }
    // MULTIPLE VIEWS + CHILDREN
    if (isMultipleViewsWithChildren(blueprint)) {
      const { path, children, ...rest } = blueprint;
      return {
        ...rest,
        path: path[locale],
        meta: {
          ...(rest.meta ?? {}),
          alternate: {
            hreflang: oppositeLocale,
            href: `${alternateBaseUrl}${path[oppositeLocale]}`,
          } as AlternateMeta,
        },
        children: generateRoutes(children, locale),
      };
    }
    // SINGLE VIEW + CHILDREN
    if (isSingleViewWithChildren(blueprint)) {
      const { path, children, ...rest } = blueprint;
      return {
        ...rest,
        path: path[locale],
        meta: {
          ...(rest.meta ?? {}),
          alternate: {
            hreflang: oppositeLocale,
            href: `${alternateBaseUrl}${path[oppositeLocale]}`,
          } as AlternateMeta,
        },
        children: generateRoutes(children, locale),
      };
    }
    // MULTIPLE VIEWS (NO CHILDREN)
    if ('components' in blueprint) {
      const { path, ...rest } = blueprint;
      return {
        ...rest,
        path: path[locale],
        meta: {
          ...(rest.meta ?? {}),
          alternate: {
            hreflang: oppositeLocale,
            href: `${alternateBaseUrl}${path[oppositeLocale]}`,
          } as AlternateMeta,
        },
      };
    }
    // SINGLE VIEW (NO CHILDREN)
    if ('component' in blueprint) {
      const { path, ...rest } = blueprint;
      return {
        ...rest,
        path: path[locale],
        meta: {
          ...(rest.meta ?? {}),
          alternate: {
            hreflang: oppositeLocale,
            href: `${alternateBaseUrl}${path[oppositeLocale]}`,
          } as AlternateMeta,
        },
      };
    }
    // Should never get here, but satisfies type checker
    throw new Error('Unknown blueprint type');
  });
}

function mergeRouteParamsIntoHref(route: RouteLocationNormalizedLoadedGeneric, href: string): string {
  const location = getLocation();
  const url = new URL(href, location.origin);

  Object.entries(route.query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      url.searchParams.delete(key);
      value.forEach((v) => {
        if (typeof v === 'string') {
          return url.searchParams.append(key, v);
        }
      });
    } else if (value != null) {
      url.searchParams.set(key, value);
    }
  });

  if (route.hash && route.hash !== '#') {
    url.hash = route.hash.startsWith('#') ? route.hash : `#${route.hash}`;
  }

  return url.toString();
}

function interpolateSlugIdPath(path: string, params: Record<string, string | string[] | undefined>): string {
  return path.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)(\([^)]+\))?/g, (_, key) => {
    const value = params[key];
    if (Array.isArray(value)) return value[0] ? encodeURIComponent(value[0]) : '';
    return value !== undefined ? encodeURIComponent(String(value)) : '';
  });
}

const ROUTES_WITH_DYNAMIC_PARAMS = ['project', 'builder', 'editorial-category', 'editorial-location', 'editorial-child-location', 'builders-location', 'guides-author'];
const ROUTES_WITH_PAGE_ALTERNATES = ['article', 'guides-category'];

export function getAlternateLink(route: RouteLocationNormalizedLoadedGeneric): AlternateMeta | undefined {
  const alt = route.meta?.alternate as AlternateMeta | undefined;
  if (!alt || ROUTES_WITH_PAGE_ALTERNATES.includes(route.name as string)) {
    return undefined;
  }

  let href;
  if (ROUTES_WITH_DYNAMIC_PARAMS.includes(route.name as string)) {
    href = interpolateSlugIdPath(alt.href, route.params);
  } else {
    href = alt.href;
  }

  const mergedHref = mergeRouteParamsIntoHref(route, href);

  return {
    hreflang: alt.hreflang,
    href: mergedHref,
  };
}

// Le slug d'un article change d'une langue à l'autre et une langue peut ne pas être publiée :
// on relit l'article, puis on retombe sur la fiche du constructeur, ou sur l'accueil des guides
// pour un guide sans constructeur, quand l'autre langue manque.
export async function getArticleAlternateLink(route: RouteLocationNormalizedLoadedGeneric): Promise<AlternateMeta | undefined> {
  if (route.name !== 'article') {
    return undefined;
  }

  try {
    const response = await getArticle({ path: { articleId: Number(route.params.articleId) } });
    if (!response.response?.ok || !response.data) {
      return undefined;
    }

    const oppositeLocale = OPPOSITE_LOCALE[determineLocale()];
    const alternateBaseUrl = getAlternateBaseUrl();
    const article = response.data;
    let href: string;
    if (article.alternate) {
      href = `${alternateBaseUrl}/guide/${article.alternate.slug}-${article.id}`;
    } else if (article.builder) {
      href = `${alternateBaseUrl}/${oppositeLocale === 'en-CA' ? 'directory' : 'repertoire'}/${article.builder.name.toSlug()}-${article.builder.id}`;
    } else {
      href = `${alternateBaseUrl}${GUIDES_ROOT[oppositeLocale]}`;
    }

    return { hreflang: oppositeLocale, href };
  } catch (error) {
    console.error('Error fetching article alternate link:', error);
  }

  return undefined;
}

export function buildGuideCategoryAlternateLink(alternateSlug: string | null): AlternateMeta {
  const oppositeLocale = OPPOSITE_LOCALE[determineLocale()];
  const root = `${getAlternateBaseUrl()}${GUIDES_ROOT[oppositeLocale]}`;

  return { hreflang: oppositeLocale, href: alternateSlug ? `${root}/${alternateSlug}` : root };
}

// Le slug d'une catégorie de guides change d'une langue à l'autre : sans traduction, on retombe sur l'accueil des guides.
export async function getGuideCategoryAlternateLink(route: RouteLocationNormalizedLoadedGeneric): Promise<AlternateMeta | undefined> {
  if (route.name !== 'guides-category') {
    return undefined;
  }

  try {
    const response = await getArticleCategory({ path: { articleCategorySlug: route.params.categorySlug as string } });
    const category = response.response?.ok && response.data ? response.data : null;

    return buildGuideCategoryAlternateLink(category?.alternateSlug ?? null);
  } catch (error) {
    console.error('Error fetching guide category alternate link:', error);
  }

  return undefined;
}

export async function getEditorialAlternateLink(route: RouteLocationNormalizedLoadedGeneric): Promise<AlternateMeta | undefined> {
  const currentLocale = determineLocale();
  const oppositeLocale = OPPOSITE_LOCALE[currentLocale];

  if (route.name === 'editorial-category' || route.name === 'editorial-location' || route.name === 'editorial-child-location' || route.name === 'builders-location') {
    try {
      let alternateSlug;

      if (route.name === 'editorial-category') {
        const response = await getAlternateCategorySlug({
          path: { editorialCategorySlug: route.params.categorySlug as string },
        });

        if (response.response?.ok && response.data) {
          alternateSlug = response.data;
        }
      } else if (route.name === 'editorial-location' || route.name === 'builders-location') {
        const response = await getAlternateLocationSlug({
          path: {
            editorialCategorySlug: route.params.categorySlug as string,
            editorialLocationSlug: route.params.locationSlug as string,
          },
        });

        if (response.response?.ok && response.data) {
          alternateSlug = response.data;
        }
      } else if (route.name === 'editorial-child-location') {
        const response = await getAlternateChildLocationSlug({
          path: {
            editorialCategorySlug: route.params.categorySlug as string,
            parentEditorialLocationSlug: route.params.parentLocationSlug as string,
            editorialLocationSlug: route.params.childLocationSlug as string,
          },
        });

        if (response.response?.ok && response.data) {
          alternateSlug = response.data;
        }
      }

      if (alternateSlug) {
        const alternateBaseUrl = getAlternateBaseUrl();
        let href;
        if (route.name === 'editorial-category') {
          href = `${alternateBaseUrl}/editorial/${alternateSlug.editorialCategorySlug}`;
        } else if (route.name === 'editorial-location') {
          href = `${alternateBaseUrl}/editorial/${alternateSlug.editorialCategorySlug}/${alternateSlug.editorialLocationSlug}`;
        } else if (route.name === 'editorial-child-location' && alternateSlug.parentEditorialLocationSlug) {
          href = `${alternateBaseUrl}/editorial/${alternateSlug.editorialCategorySlug}/${alternateSlug.parentEditorialLocationSlug}/${alternateSlug.editorialLocationSlug}`;
        } else if (route.name === 'builders-location') {
          href = `${alternateBaseUrl}${BUILDERS_ROOT[oppositeLocale]}/${alternateSlug.editorialCategorySlug}/${alternateSlug.editorialLocationSlug}`;
        }

        return {
          hreflang: oppositeLocale,
          href: href!,
        };
      }
    } catch (error) {
      console.error('Error fetching editorial alternate slug:', error);
    }
  }

  return undefined;
}
